/**
 * Micheals Infrastructure Advisory - TERMS OF ENGAGEMENT
 * VERSION: 6.1.0 (Governance & Fiduciary Framework)
 * FILE: src/pages/TermsOfService.js
 * ROLE: Legal framework establishing the firm as a non-service entity.
 */

import React from 'react';
import SEO from '../components/SEO';
import { businessConfig as BusinessInfo } from '../config/businessConfig';

const TermsOfService = () => {
  return (
    <div className="bg-white min-h-screen py-24 px-6 selection:bg-amber-100 selection:text-amber-950">
      <SEO 
        title={`Terms of Engagement | ${BusinessInfo.name}`} 
        description={`Legal framework and professional disclosures for ${BusinessInfo.name} Infrastructure Governance & Consultation.`} 
      />
      
      <div className="max-w-3xl mx-auto">
        <header className="mb-16 border-b border-stone-200 pb-8">
          <h1 className="text-4xl md:text-5xl font-serif text-stone-950 mb-4 italic">
            Terms of <span className="not-italic font-light text-stone-400">Engagement.</span>
          </h1>
          <p className="text-amber-800 text-[10px] uppercase tracking-[0.3em] font-black">
            Governance Framework • Effective {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </header>

        <div className="space-y-12">
          {/* Section 1: Nature of Engagement */}
          <section>
            <h2 className="text-[10px] font-black text-stone-950 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-800"></span>
              1. Nature of Engagement
            </h2>
            <p className="text-stone-600 leading-relaxed font-light text-sm">
              By engaging with our platform and consultation protocols, you acknowledge that <strong>{BusinessInfo.name}</strong> operates exclusively as a <strong>Technical Governance and Infrastructure Advisory firm.</strong> We do not perform field labor, physical installations, or construction services. Our mandate is the provision of forensic auditing, architectural oversight, and high-fidelity design logic.
            </p>
          </section>

          {/* Section 2: Technical Directive vs. Execution */}
          <section>
            <h2 className="text-[10px] font-black text-stone-950 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-800"></span>
              2. Technical Directive vs. Execution
            </h2>
            <div className="space-y-4">
              <p className="text-stone-600 leading-relaxed font-light text-sm">
                Our primary deliverables are <strong>Technical Directives</strong> (blueprints, design briefs, and forensic audits). These serve as the authoritative logic for your asset.
              </p>
              <blockquote className="bg-stone-50 border-l-2 border-amber-800 p-6 text-stone-600 italic font-light text-sm rounded-r-sm">
                "All physical implementation, structural modifications, and site-level integration must be executed by independent, licensed, and insured contracting partners. The selection and site-level oversight of these entities remain the responsibility of the Principal. {BusinessInfo.name} provides independent administrative liaison services specifically to ensure design fidelity and adherence to technical specifications."
              </blockquote>
            </div>
          </section>

          {/* Section 3: Intellectual Property & Logic */}
          <section>
            <h2 className="text-[10px] font-black text-stone-950 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-800"></span>
              3. Intellectual Property & System Logic
            </h2>
            <p className="text-stone-600 leading-relaxed font-light text-sm">
              All proprietary system architectures, custom control logic, and diagnostic frameworks developed by {BusinessInfo.name} remain the exclusive intellectual property of the firm. Engagement grants a single-use license for the specified asset only; it does not authorize the redistribution, replication, or commercialization of our technical blueprints without express written governance.
            </p>
          </section>

          {/* Section 4: Definitive Liability Limitation */}
          <section>
            <h2 className="text-[10px] font-black text-stone-950 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-800"></span>
              4. Definitive Liability Limitation
            </h2>
            <p className="text-stone-600 leading-relaxed font-light text-sm mb-6">
              {BusinessInfo.name} shall not be held liable for property damage, operational failure, or personal injury resulting from work performed by third-party contractors or for deviations from our provided Technical Directives.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="p-6 bg-stone-50 border border-stone-200">
                  <p className="text-[9px] font-black uppercase text-stone-400 mb-2">Our Responsibility</p>
                  <p className="text-xs text-stone-950 font-black">Design Integrity, System Logic, & Administrative Stewardship.</p>
               </div>
               <div className="p-6 bg-stone-50 border border-stone-200">
                  <p className="text-[9px] font-black uppercase text-stone-400 mb-2">Contractor Responsibility</p>
                  <p className="text-xs text-stone-950 font-black">Site Safety, Regulatory Compliance, Permits, & Physical Execution.</p>
               </div>
            </div>
          </section>

          {/* Section 5: Governing Jurisdiction */}
          <section className="pt-8 border-t border-stone-200">
            <h2 className="text-[10px] font-black text-stone-950 mb-6 uppercase tracking-[0.2em] flex items-center gap-3">
              <span className="w-8 h-[1px] bg-amber-800"></span>
              5. Governing Jurisdiction
            </h2>
            <p className="text-stone-600 leading-relaxed font-light text-sm">
              This engagement framework is governed by the laws of North Carolina. Any disputes regarding architectural logic or administrative performance shall be resolved through professional mediation to ensure the underlying technical nuances are correctly weighed before legal escalation.
            </p>
          </section>
        </div>
        
        <footer className="mt-24 pt-8 border-t border-stone-200 text-center">
          <p className="text-stone-400 text-[9px] font-black uppercase tracking-widest">
            © {new Date().getFullYear()} {BusinessInfo.name} • Infrastructure Governance & Stewardship
          </p>
        </footer>
      </div>
    </div>
  );
};

export default TermsOfService;